import React, { useEffect, useState } from "react";
import "./Settings.css";

import Loader from "../../Loader/Loader";

import {
  getSettings,
  updateGeneralSettings,
  updatePricingSettings,
  updateDeliverySettings,
  updateLegalSettings,
} from "../../service/Mainservice";

import { useNavigate } from "react-router";

import SettingsIcon from "@mui/icons-material/Settings";
import BusinessIcon from "@mui/icons-material/Business";
import PaymentsIcon from "@mui/icons-material/Payments";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GavelIcon from "@mui/icons-material/Gavel";
import EditIcon from "@mui/icons-material/Edit";
import LanguageIcon from "@mui/icons-material/Language";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import SecurityIcon from "@mui/icons-material/Security";
import HistoryIcon from "@mui/icons-material/History";

const Settings = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState(null);

  const [activeModal, setActiveModal] = useState("");
  const [legalTab, setLegalTab] = useState("terms");
  const [generalForm, setGeneralForm] = useState({
    appName: "",
    appLogo: "",
    contactEmail: "",
    contactPhone: "",
    contactAddress: "",
    supportedCities: [],
    supportedLanguages: [],
  });

  const [pricingForm, setPricingForm] = useState({
    commissionRates: "",
    deliveryFeeBase: "",
    deliveryFeePerMile: "",
    deliveryFeeSurge: "",
    serviceFeePercent: "",
    driverPayoutRate: "",
  });

  const [deliveryForm, setDeliveryForm] = useState({
    maxDeliveryDistance: "",
    autoAssignmentRule: "",
    orderTimeoutLimits: "",
  });

  const [legalForm, setLegalForm] = useState({
    termsOfService: "",
    privacyPolicy: "",
    contracts: "",
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);

      const response = await getSettings();

      const data = response.data.data;

      setSettings(data);

      setGeneralForm({
        appName: data.appName || "",
        appLogo: data.appLogo || "",
        contactEmail: data.contactEmail || "",
        contactPhone: data.contactPhone || "",
        contactAddress: data.contactAddress || "",
        supportedCities: data.supportedCities
          ? JSON.parse(data.supportedCities)
          : [],
        supportedLanguages: data.supportedLanguages
          ? JSON.parse(data.supportedLanguages)
          : [],
      });

      setPricingForm({
        commissionRates: data.commissionRates || "{}",
        deliveryFeeBase: data.deliveryFeeBase || "",
        deliveryFeePerMile: data.deliveryFeePerMile || "",
        deliveryFeeSurge: data.deliveryFeeSurge || "",
        serviceFeePercent: data.serviceFeePercent || "",
        driverPayoutRate: data.driverPayoutRate || "",
      });

      setDeliveryForm({
        maxDeliveryDistance: data.maxDeliveryDistance || "",
        autoAssignmentRule: data.autoAssignmentRule || "",
        orderTimeoutLimits: data.orderTimeoutLimits || "",
      });

      setLegalForm({
        termsOfService: data.termsOfService || "",
        privacyPolicy: data.privacyPolicy || "",
        contracts: data.contracts || "",
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);

      if (error.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  const openModal = (type) => {
    setActiveModal(type);
  };

  const closeModal = () => {
    setActiveModal("");
  }; /* ============================================
      SAVE GENERAL SETTINGS
  ============================================= */

  const saveGeneralSettings = async () => {
    try {
      setLoading(true);

      await updateGeneralSettings({
        ...generalForm,
      });

      closeModal();
      loadSettings();
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  /* ============================================
      SAVE PRICING SETTINGS
  ============================================= */

  const savePricingSettings = async () => {
    try {
      setLoading(true);

      await updatePricingSettings({
        ...pricingForm,
      });

      closeModal();
      loadSettings();
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  /* ============================================
      SAVE DELIVERY SETTINGS
  ============================================= */

  const saveDeliverySettings = async () => {
    try {
      setLoading(true);

      await updateDeliverySettings({
        ...deliveryForm,
      });

      closeModal();
      loadSettings();
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  /* ============================================
      SAVE LEGAL SETTINGS
  ============================================= */

  const saveLegalSettings = async () => {
    try {
      setLoading(true);

      await updateLegalSettings({
        ...legalForm,
      });

      closeModal();
      loadSettings();
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  /* ============================================
      HELPERS
  ============================================= */

  const versionHistory = settings?.versionHistory
    ? JSON.parse(settings.versionHistory)
    : [];

  const permissions = settings?.rolePermissions
    ? JSON.parse(settings.rolePermissions)
    : {};

  const cities = settings?.supportedCities
    ? JSON.parse(settings.supportedCities)
    : [];

  const languages = settings?.supportedLanguages
    ? JSON.parse(settings.supportedLanguages)
    : [];

  const commission =
    settings?.commissionRates && settings.commissionRates !== "{}"
      ? JSON.parse(settings.commissionRates)
      : {};

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    return `$${Number(value).toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    return `${value}%`;
  };

  /* ============================================
      RETURN
  ============================================= */

  return (
    <>
      <div className="settings_page">
        {/* ================= HEADER ================= */}
        <div className="settings_header">
          <div>
            <h1>
              <SettingsIcon />
              Platform Settings
            </h1>

            <p>
              Configure your marketplace, pricing, delivery and legal settings.
            </p>
          </div>
        </div>
        {/* ================= OVERVIEW ================= */}
        <div className="overview_card">
          <div className="overview_left">
            <div className="logo_box">
              {settings?.appLogo ? (
                <img src={settings.appLogo} alt="logo" />
              ) : (
                <BusinessIcon
                  sx={{
                    fontSize: 45,
                  }}
                />
              )}
            </div>

            <div>
              <h2>{settings?.appName}</h2>

              <p>{settings?.contactEmail}</p>

              <p>{settings?.contactPhone || "-"}</p>
            </div>
          </div>

          <div className="overview_right">
            <div>
              <label>Version</label>

              <span>
                {versionHistory.length
                  ? versionHistory[versionHistory.length - 1].version
                  : "-"}
              </span>
            </div>

            <div>
              <label>Updated</label>

              <span>
                {settings?.updatedAt
                  ? new Date(settings.updatedAt).toLocaleDateString()
                  : "-"}
              </span>
            </div>
          </div>
        </div>
        {/* ================= MAIN GRID ================= */}
        <div className="settings_grid">
          {" "}
          {/* ===========================
                GENERAL CONFIGURATION
          ============================ */}
          <div className="settings_card">
            <div className="card_header">
              <div className="card_title">
                <BusinessIcon />

                <div>
                  <h3>General Configuration</h3>

                  <p>App information, contact details, cities & languages</p>
                </div>
              </div>

              <button className="edit_btn" onClick={() => openModal("general")}>
                <EditIcon fontSize="small" />
                Edit
              </button>
            </div>

            <div className="settings_info">
              <div className="info_item">
                <label>Application Name</label>

                <span>{settings?.appName || "-"}</span>
              </div>

              <div className="info_item">
                <label>Contact Email</label>

                <span>{settings?.contactEmail || "-"}</span>
              </div>

              <div className="info_item">
                <label>Phone Number</label>

                <span>{settings?.contactPhone || "-"}</span>
              </div>

              <div className="info_item">
                <label>Address</label>

                <span>{settings?.contactAddress || "-"}</span>
              </div>
            </div>

            <div className="chip_section">
              <h4>
                <LocationCityIcon />
                Supported Cities
              </h4>

              <div className="chips">
                {cities.length > 0 ? (
                  cities.map((city, index) => (
                    <span className="chip" key={index}>
                      {city}
                    </span>
                  ))
                ) : (
                  <span className="empty_chip">No Cities Added</span>
                )}
              </div>
            </div>

            <div className="chip_section">
              <h4>
                <LanguageIcon />
                Supported Languages
              </h4>

              <div className="chips">
                {languages.length > 0 ? (
                  languages.map((lang, index) => (
                    <span className="chip" key={index}>
                      {lang}
                    </span>
                  ))
                ) : (
                  <span className="empty_chip">No Languages Added</span>
                )}
              </div>
            </div>
          </div>
          {/* ===========================
                PRICING & FEES
          ============================ */}
          <div className="settings_card">
            <div className="card_header">
              <div className="card_title">
                <PaymentsIcon />

                <div>
                  <h3>Pricing & Fees</h3>

                  <p>Commission, delivery charges, service fees & payouts</p>
                </div>
              </div>

              <button className="edit_btn" onClick={() => openModal("pricing")}>
                <EditIcon fontSize="small" />
                Edit
              </button>
            </div>

            <div className="settings_info">
              <div className="info_item">
                <label>Base Delivery Fee</label>

                <span>{formatCurrency(settings?.deliveryFeeBase)}</span>
              </div>

              <div className="info_item">
                <label>Fee Per Mile</label>

                <span>{formatCurrency(settings?.deliveryFeePerMile)}</span>
              </div>

              <div className="info_item">
                <label>Surge Fee</label>

                <span>{formatCurrency(settings?.deliveryFeeSurge)}</span>
              </div>

              <div className="info_item">
                <label>Service Fee</label>

                <span>{formatPercent(settings?.serviceFeePercent)}</span>
              </div>

              <div className="info_item">
                <label>Driver Payout</label>

                <span>{formatPercent(settings?.driverPayoutRate)}</span>
              </div>
            </div>
          </div>{" "}
          {/* ===========================
                DELIVERY SETTINGS
          ============================ */}
          <div className="settings_card">
            <div className="card_header">
              <div className="card_title">
                <LocalShippingIcon />

                <div>
                  <h3>Delivery Settings</h3>

                  <p>
                    Delivery limits, assignment rules & timeout configuration
                  </p>
                </div>
              </div>

              <button
                className="edit_btn"
                onClick={() => openModal("delivery")}
              >
                <EditIcon fontSize="small" />
                Edit
              </button>
            </div>

            <div className="settings_info">
              <div className="info_item">
                <label>Maximum Delivery Distance</label>

                <span>{settings?.maxDeliveryDistance} Miles</span>
              </div>

              <div className="info_item">
                <label>Auto Assignment Rule</label>

                <span>
                  {settings?.autoAssignmentRule
                    ?.replaceAll("_", " ")
                    ?.replace(/\b\w/g, (c) => c.toUpperCase())}
                </span>
              </div>

              <div className="info_item">
                <label>Order Timeout</label>

                <span>{settings?.orderTimeoutLimits} Seconds</span>
              </div>
            </div>
          </div>
          {/* ===========================
                LEGAL SETTINGS
          ============================ */}
          <div className="settings_card">
            <div className="card_header">
              <div className="card_title">
                <GavelIcon />

                <div>
                  <h3>Legal Settings</h3>

                  <p>Terms of service, privacy policy & contracts</p>
                </div>
              </div>

              <button className="edit_btn" onClick={() => openModal("legal")}>
                <EditIcon fontSize="small" />
                Edit
              </button>
            </div>

            <div className="legal_preview">
              <div>
                <h4>Terms of Service</h4>

                <p>
                  {settings?.termsOfService
                    ? settings.termsOfService.substring(0, 150)
                    : "Not Available"}

                  {settings?.termsOfService?.length > 150 && "..."}
                </p>
              </div>

              <div>
                <h4>Privacy Policy</h4>

                <p>
                  {settings?.privacyPolicy
                    ? settings.privacyPolicy.substring(0, 150)
                    : "Not Available"}

                  {settings?.privacyPolicy?.length > 150 && "..."}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* ===========================
              ROLE PERMISSIONS
        ============================ */}
        <div className="settings_card permission_card">
          <div className="card_header">
            <div className="card_title">
              <SecurityIcon />

              <div>
                <h3>Role Permissions</h3>

                <p>Marketplace access for each user role</p>
              </div>
            </div>
          </div>

          <div className="permission_grid">
            {Object.keys(permissions).map((role) => (
              <div className="permission_box" key={role}>
                <h4>
                  {role}

                  <span>{permissions[role].length}</span>
                </h4>

                <div className="permission_tags">
                  {permissions[role].length > 0 ? (
                    permissions[role].map((item, index) => (
                      <span className="permission_chip" key={index}>
                        {item.replaceAll("_", " ")}
                      </span>
                    ))
                  ) : (
                    <span className="empty_chip">No Permissions</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* ===========================
              VERSION HISTORY
        ============================ */}
        <div className="settings_card version_card">
          <div className="card_header">
            <div className="card_title">
              <HistoryIcon />

              <div>
                <h3>Version History</h3>

                <p>Platform updates & changelog</p>
              </div>
            </div>
          </div>

          <div className="version_timeline">
            {versionHistory.length > 0 ? (
              versionHistory.map((item, index) => (
                <div className="timeline_item" key={index}>
                  <div className="timeline_dot" />

                  <div className="timeline_content">
                    <h4>{item.version}</h4>

                    <p>{item.changelog}</p>

                    <small>{new Date(item.updatedAt).toLocaleString()}</small>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty_state">No Version History Available</div>
            )}
          </div>
        </div>{" "}
        {/* ===========================
              GENERAL SETTINGS MODAL
        ============================ */}
        {activeModal === "general" && (
          <div className="settings_modal_overlay">
            <div className="settings_modal">
              <div className="modal_header">
                <h2>Edit General Configuration</h2>

                <button className="close_btn" onClick={closeModal}>
                  ✕
                </button>
              </div>

              <div className="modal_body">
                <div className="form_group">
                  <label>Application Name</label>

                  <input
                    type="text"
                    value={generalForm.appName}
                    onChange={(e) =>
                      setGeneralForm({
                        ...generalForm,
                        appName: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form_group">
                  <label>Logo URL</label>

                  <input
                    type="text"
                    value={generalForm.appLogo}
                    onChange={(e) =>
                      setGeneralForm({
                        ...generalForm,
                        appLogo: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form_group">
                  <label>Email</label>

                  <input
                    type="email"
                    value={generalForm.contactEmail}
                    onChange={(e) =>
                      setGeneralForm({
                        ...generalForm,
                        contactEmail: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form_group">
                  <label>Phone</label>

                  <input
                    type="text"
                    value={generalForm.contactPhone}
                    onChange={(e) =>
                      setGeneralForm({
                        ...generalForm,
                        contactPhone: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form_group">
                  <label>Address</label>

                  <textarea
                    rows="3"
                    value={generalForm.contactAddress}
                    onChange={(e) =>
                      setGeneralForm({
                        ...generalForm,
                        contactAddress: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form_group">
                  <label>Supported Cities</label>

                  <input
                    type="text"
                    placeholder="New York, Dallas, Austin"
                    value={generalForm.supportedCities.join(", ")}
                    onChange={(e) =>
                      setGeneralForm({
                        ...generalForm,
                        supportedCities: e.target.value
                          .split(",")
                          .map((item) => item.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </div>

                <div className="form_group">
                  <label>Supported Languages</label>

                  <input
                    type="text"
                    placeholder="English, Spanish"
                    value={generalForm.supportedLanguages.join(", ")}
                    onChange={(e) =>
                      setGeneralForm({
                        ...generalForm,
                        supportedLanguages: e.target.value
                          .split(",")
                          .map((item) => item.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </div>
              </div>

              <div className="modal_footer">
                <button className="cancel_btn" onClick={closeModal}>
                  Cancel
                </button>

                <button className="save_btn" onClick={saveGeneralSettings}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ===========================
              PRICING MODAL
        ============================ */}
        {activeModal === "pricing" && (
          <div className="settings_modal_overlay">
            <div className="settings_modal">
              <div className="modal_header">
                <h2>Edit Pricing & Fees</h2>

                <button className="close_btn" onClick={closeModal}>
                  ✕
                </button>
              </div>

              <div className="modal_body">
                <div className="form_group">
                  <label>Commission Rates </label>

                  <input
                    type="number"
                    value={pricingForm.commissionRates}
                    onChange={(e) =>
                      setPricingForm({
                        ...pricingForm,
                        commissionRates: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form_group">
                  <label>Base Delivery Fee</label>

                  <input
                    type="number"
                    value={pricingForm.deliveryFeeBase}
                    onChange={(e) =>
                      setPricingForm({
                        ...pricingForm,
                        deliveryFeeBase: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form_group">
                  <label>Fee Per Mile</label>

                  <input
                    type="number"
                    value={pricingForm.deliveryFeePerMile}
                    onChange={(e) =>
                      setPricingForm({
                        ...pricingForm,
                        deliveryFeePerMile: e.target.value,
                      })
                    }
                  />
                </div>{" "}
                <div className="form_group">
                  <label>Surge Fee</label>

                  <input
                    type="number"
                    value={pricingForm.deliveryFeeSurge}
                    onChange={(e) =>
                      setPricingForm({
                        ...pricingForm,
                        deliveryFeeSurge: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form_group">
                  <label>Service Fee (%)</label>

                  <input
                    type="number"
                    value={pricingForm.serviceFeePercent}
                    onChange={(e) =>
                      setPricingForm({
                        ...pricingForm,
                        serviceFeePercent: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form_group">
                  <label>Driver Payout Rate (%)</label>

                  <input
                    type="number"
                    value={pricingForm.driverPayoutRate}
                    onChange={(e) =>
                      setPricingForm({
                        ...pricingForm,
                        driverPayoutRate: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="modal_footer">
                <button className="cancel_btn" onClick={closeModal}>
                  Cancel
                </button>

                <button className="save_btn" onClick={savePricingSettings}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ===========================
              DELIVERY SETTINGS MODAL
        ============================ */}
        {activeModal === "delivery" && (
          <div className="settings_modal_overlay">
            <div className="settings_modal">
              <div className="modal_header">
                <h2>Edit Delivery Settings</h2>

                <button className="close_btn" onClick={closeModal}>
                  ✕
                </button>
              </div>

              <div className="modal_body">
                <div className="form_group">
                  <label>Maximum Delivery Distance (Miles)</label>

                  <input
                    type="number"
                    value={deliveryForm.maxDeliveryDistance}
                    onChange={(e) =>
                      setDeliveryForm({
                        ...deliveryForm,
                        maxDeliveryDistance: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form_group">
                  <label>Auto Assignment Rule</label>

                  <select
                    value={deliveryForm.autoAssignmentRule}
                    onChange={(e) =>
                      setDeliveryForm({
                        ...deliveryForm,
                        autoAssignmentRule: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Rule</option>

                    <option value="round_robin">Round Robin</option>

                    <option value="closest_first">Closest Driver First</option>

                    <option value="least_busy">Least Busy Driver</option>

                    <option value="manual">Manual Assignment</option>
                  </select>
                </div>

                <div className="form_group">
                  <label>Order Timeout (Seconds)</label>

                  <input
                    type="number"
                    value={deliveryForm.orderTimeoutLimits}
                    onChange={(e) =>
                      setDeliveryForm({
                        ...deliveryForm,
                        orderTimeoutLimits: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="modal_footer">
                <button className="cancel_btn" onClick={closeModal}>
                  Cancel
                </button>

                <button className="save_btn" onClick={saveDeliverySettings}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>{" "}
      {/* =========================== LEGAL SETTINGS MODAL ============================ */}
      {activeModal === "legal" && (
        <div className="settings_modal_overlay">
          <div className="settings_modal legal_modal">
            <div className="modal_header">
              <div>
                <h2>Legal Documents</h2>
                <p>Manage Terms of Service, Privacy Policy and Contracts.</p>
              </div>

              <button className="close_btn" onClick={closeModal}>
                ✕
              </button>
            </div>

            <div className="legal_tabs">
              <button
                className={legalTab === "terms" ? "legal_active" : ""}
                onClick={() => setLegalTab("terms")}
              >
                Terms of Service
              </button>

              <button
                className={legalTab === "privacy" ? "legal_active" : ""}
                onClick={() => setLegalTab("privacy")}
              >
                Privacy Policy
              </button>

              <button
                className={legalTab === "contracts" ? "legal_active" : ""}
                onClick={() => setLegalTab("contracts")}
              >
                Contracts
              </button>
            </div>

            <div className="modal_body">
              {legalTab === "terms" && (
                <div className="form_group">
                  <label>Terms of Service</label>

                  <textarea
                    rows={16}
                    value={legalForm.termsOfService}
                    onChange={(e) =>
                      setLegalForm({
                        ...legalForm,
                        termsOfService: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {legalTab === "privacy" && (
                <div className="form_group">
                  <label>Privacy Policy</label>

                  <textarea
                    rows={16}
                    value={legalForm.privacyPolicy}
                    onChange={(e) =>
                      setLegalForm({
                        ...legalForm,
                        privacyPolicy: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {legalTab === "contracts" && (
                <div className="form_group">
                  <label>Contracts</label>

                  <textarea
                    rows={16}
                    value={legalForm.contracts}
                    onChange={(e) =>
                      setLegalForm({
                        ...legalForm,
                        contracts: e.target.value,
                      })
                    }
                  />
                </div>
              )}
            </div>

            <div className="modal_footer">
              <button className="cancel_btn" onClick={closeModal}>
                Cancel
              </button>

              <button className="save_btn" onClick={saveLegalSettings}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      {/* =========================== LOADER ============================ */}
      {/* {loading &&

} */}
    </>
  );
};

export default Settings;
